/**
 * Påminnelser för kundtoppnod: besiktning, fordonsskatt och leasing.
 */

import React from 'react';
import { useTranslation } from '@/store/languageStore';
import type { ReminderServiceSettings, ReminderSettings } from '@/types/organizations';
import { DEFAULT_REMINDER_SETTINGS, mergeReminderOrgSettings } from '@/utils/organizationReminderDefaults';

interface Props {
  value: ReminderSettings | undefined | null;
  onChange: (next: ReminderSettings) => void;
}

function Check({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className={`flex items-center gap-2 text-sm text-textDefault ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
      />
      <span>{label}</span>
    </label>
  );
}

const OrganizationReminderSettings: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const s = mergeReminderOrgSettings(value);

  const patchService = (key: 'inspection' | 'tax', next: Partial<ReminderServiceSettings>) => {
    const current = s[key];
    const merged = { ...current, ...next };
    if (!merged.enabled) merged.notify = false;
    onChange({
      ...DEFAULT_REMINDER_SETTINGS,
      ...s,
      [key]: merged,
    });
  };

  const row = (key: 'inspection' | 'tax', title: string, help: string) => {
    const svc = s[key];
    return (
      <div className="p-4 rounded-lg border border-border bg-bgContainer space-y-3">
        <div>
          <h4 className="text-sm font-medium text-textDefault">{title}</h4>
          <p className="text-xs text-textSecondary mt-0.5">{help}</p>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <Check
            checked={svc.enabled}
            label={t('organizations.reminders.service')}
            onChange={(enabled) => patchService(key, { enabled })}
          />
          <Check
            checked={svc.notify}
            disabled={!svc.enabled}
            label={t('organizations.reminders.notify')}
            onChange={(notify) => patchService(key, { notify })}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-4 rounded-lg border border-border bg-bgElevated">
      <div>
        <h3 className="text-base font-semibold text-textDefault mb-1">{t('organizations.reminders.title')}</h3>
        <p className="text-sm text-textSecondary">{t('organizations.reminders.intro')}</p>
      </div>
      {row('inspection', t('organizations.reminders.inspection'), t('organizations.reminders.inspectionHelp'))}
      {row('tax', t('organizations.reminders.tax'), t('organizations.reminders.taxHelp'))}
      <div className="p-4 rounded-lg border border-border bg-bgContainer space-y-3">
        <div>
          <h4 className="text-sm font-medium text-textDefault">{t('organizations.reminders.leasing')}</h4>
          <p className="text-xs text-textSecondary mt-0.5">{t('organizations.reminders.leasingHelp')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <Check
            checked={s.leasing.enabled}
            label={t('organizations.reminders.service')}
            onChange={(enabled) =>
              onChange({
                ...DEFAULT_REMINDER_SETTINGS,
                ...s,
                leasing: { ...s.leasing, enabled, notify: enabled ? s.leasing.notify : false },
              })
            }
          />
          <Check
            checked={s.leasing.notify}
            disabled={!s.leasing.enabled}
            label={t('organizations.reminders.notify')}
            onChange={(notify) =>
              onChange({
                ...DEFAULT_REMINDER_SETTINGS,
                ...s,
                leasing: { ...s.leasing, notify },
              })
            }
          />
          <label className="flex items-center gap-2 text-sm text-textDefault">
            <span className="font-medium">{t('organizations.reminders.monthsAhead')}</span>
            <input
              type="number"
              min={1}
              max={24}
              value={s.leasing.monthsAhead}
              onChange={(e) =>
                onChange({
                  ...DEFAULT_REMINDER_SETTINGS,
                  ...s,
                  leasing: {
                    ...s.leasing,
                    monthsAhead: Math.min(24, Math.max(1, Number(e.target.value) || 6)),
                  },
                })
              }
              className="w-20 rounded-md border border-border bg-bgElevated px-2 py-1 text-sm"
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default OrganizationReminderSettings;
